import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { CHANNEL_QUICK_BUTTON_META } from '@/components/ChannelNameQuickButtons';
import { useI18nText } from '@/lib/utils/i18nText';

export type ChannelDrawerSection = 'details' | 'chat';

/**
 * Single source of truth for each drawer section's icon and label — consumed by
 * both this rail and `ChannelDrawerHeaderTitle`, so the rail button and the
 * header title can't drift apart. Icons come from `CHANNEL_QUICK_BUTTON_META`,
 * the same meta the quick buttons that open this drawer use.
 */
export const CHANNEL_DRAWER_SECTIONS: {
  key: ChannelDrawerSection;
  icon: string;
  iconColorClass: string;
  labelEn: string;
  labelKo: string;
}[] = [
  {
    key: 'details',
    icon: CHANNEL_QUICK_BUTTON_META.details.icon,
    iconColorClass: 'text-ggc-cta-blue',
    labelEn: 'Channel details',
    labelKo: '채널 상세',
  },
  {
    key: 'chat',
    icon: CHANNEL_QUICK_BUTTON_META.chat.icon,
    iconColorClass: 'text-ggc-green',
    labelEn: 'Chat System',
    labelKo: '채팅 시스템',
  },
];

/**
 * Left icon rail for the merged channel drawer (Channel details / Chat system) —
 * reuses the same slim icon-strip look as `CollapsedSelectSlot`'s `IconBtn`
 * (`.collapsed-sidebar-icon-btn`/`.collapsed-sidebar-icons`, see
 * CollapsedCampaignSidebar.tsx) so it reads as the same UI language.
 */
export function ChannelDrawerRail({
  active,
  onSelect,
  onEditChannel,
  editLoading,
}: {
  active: ChannelDrawerSection;
  onSelect: (section: ChannelDrawerSection) => void;
  /** Opens the shared Edit Channel dialog. Omit to hide the action. */
  onEditChannel?: () => void;
  editLoading?: boolean;
}) {
  const { t } = useI18nText();

  const railButton = (
    key: string,
    icon: string,
    label: string,
    opts: { onClick: () => void; active?: boolean; disabled?: boolean },
  ) => (
    <TooltipPrimitive.Root key={key} delayDuration={500}>
      <TooltipPrimitive.Trigger asChild>
        <button
          type="button"
          onClick={opts.onClick}
          disabled={opts.disabled}
          className={`collapsed-sidebar-icon-btn${opts.active ? ' collapsed-sidebar-icon-btn--active' : ''}`}
          aria-label={label}
          {...(opts.active !== undefined ? { 'aria-pressed': opts.active } : {})}
        >
          <i className={`fa ${icon}`} aria-hidden />
        </button>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side="right"
          sideOffset={8}
          className="z-[1300] rounded-ggc-sm bg-[#1e293b] px-2 py-1 ggc-text-caption-m text-white shadow-ggc-md"
        >
          {label}
          <TooltipPrimitive.Arrow className="fill-[#1e293b]" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );

  return (
    <nav className="collapsed-sidebar-icons channel-drawer-rail" aria-label={t('Drawer sections', '드로어 섹션')}>
      {CHANNEL_DRAWER_SECTIONS.map((item) => railButton(
        item.key,
        item.icon,
        t(item.labelEn, item.labelKo),
        { onClick: () => onSelect(item.key), active: active === item.key },
      ))}
      {onEditChannel && (
        <>
          {/* Divider: Edit is an action (opens a dialog), not a third pane to
              switch to, so it reads as its own group under the section buttons. */}
          <div className="collapsed-sidebar-divider_blue" />
          {railButton(
            'edit',
            // fa-edit, matching the Edit button inside the Channel-details pane
            // this rail sits next to (CHANNEL_QUICK_BUTTON_META.edit is fa-pencil,
            // which is right for the row quick-buttons but not here).
            editLoading ? 'fa-spinner fa-spin' : 'fa-edit',
            t('Edit Channel', '채널 수정'),
            { onClick: onEditChannel, disabled: editLoading },
          )}
        </>
      )}
    </nav>
  );
}
